import { Navigate, Route, Routes, useLocation } from 'react-router';
import { lazy, Suspense, useEffect, type ReactNode } from 'react';

import { AppLayout, AdminLayout } from '@/shared/components/AppLayout';
import { RequireAuth } from '@/shared/auth/RequireAuth';
import { RequireFeature } from '@/shared/auth/RequireFeature';
import { RequireEntitlement } from '@/shared/auth/RequireEntitlement';
import { PublicOnly } from '@/shared/auth/PublicOnly';
import { useAuth } from '@/shared/auth/AuthContext';
import { CookieBanner } from '@/shared/components/CookieBanner';
import { PortalRequireAuth } from '@/modules/portal/PortalAuthContext';
import { AuthLayout } from '@/shared/components/AuthLayout';

const DashboardPage = lazy(() => import('@/modules/dashboard/pages/DashboardPage').then((m) => ({ default: m.DashboardPage })));
const LoginPage = lazy(() => import('@/modules/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })));
const MfaChallengePage = lazy(() => import('@/modules/auth/pages/MfaChallengePage').then((m) => ({ default: m.MfaChallengePage })));
const MfaSetupPage = lazy(() => import('@/modules/auth/pages/MfaSetupPage').then((m) => ({ default: m.MfaSetupPage })));
const ManagerSelectTenantPage = lazy(() => import('@/modules/auth/pages/ManagerSelectTenantPage').then((m) => ({ default: m.ManagerSelectTenantPage })));
const ForceChangePasswordPage = lazy(() => import('@/modules/auth/pages/ForceChangePasswordPage').then((m) => ({ default: m.ForceChangePasswordPage })));
const ForgotPasswordPage = lazy(() => import('@/modules/auth/pages/ForgotPasswordPage').then((m) => ({ default: m.ForgotPasswordPage })));
const ResetPasswordPage = lazy(() => import('@/modules/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })));
const MobileOnlyPage = lazy(() => import('@/modules/auth/pages/MobileOnlyPage').then((m) => ({ default: m.MobileOnlyPage })));
const SignupPage = lazy(() => import('@/modules/signup/pages/SignupPage').then((m) => ({ default: m.SignupPage })));
const VerifyEmailPage = lazy(() => import('@/modules/signup/pages/VerifyEmailPage').then((m) => ({ default: m.VerifyEmailPage })));
const DataProcessingNoticePage = lazy(() =>
  import('@/modules/signup/pages/DataProcessingNoticePage').then((m) => ({ default: m.DataProcessingNoticePage })),
);
const SetupWizardLayout = lazy(() => import('@/modules/signup/components/SetupWizardLayout').then((m) => ({ default: m.SetupWizardLayout })));
const MobileLayout = lazy(() => import('@/modules/mobile/components/MobileLayout').then((m) => ({ default: m.MobileLayout })));
const SimpleModeLayout = lazy(() => import('@/modules/simple-mode/components/SimpleModeLayout').then((m) => ({ default: m.SimpleModeLayout })));
const SimpleModePage = lazy(() => import('@/modules/simple-mode/pages/SimpleModePage').then((m) => ({ default: m.SimpleModePage })));
const NewSheetPage = lazy(() => import('@/modules/mobile/pages/NewSheetPage').then((m) => ({ default: m.NewSheetPage })));
const MobileHistoryPage = lazy(() => import('@/modules/mobile/pages/HistoryPage').then((m) => ({ default: m.HistoryPage })));
const MobilePickupsPage = lazy(() => import('@/modules/mobile/pages/PickupsPage').then((m) => ({ default: m.PickupsPage })));
const MobileContainersPage = lazy(() => import('@/modules/mobile/pages/ContainersPage').then((m) => ({ default: m.ContainersPage })));
const NewPersonScanPage = lazy(() => import('@/modules/mobile/pages/NewPersonScanPage').then((m) => ({ default: m.NewPersonScanPage })));
const MobileCheckInPage = lazy(() => import('@/modules/mobile/pages/CheckInPage').then((m) => ({ default: m.CheckInPage })));
const SettingsPage = lazy(() => import('@/modules/settings/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })));
const SecurityPage = lazy(() => import('@/modules/settings/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })));
const NotFoundPage = lazy(() => import('@/shared/components/NotFoundPage').then((m) => ({ default: m.NotFoundPage })));
const WastesPage = lazy(() => import('@/modules/catalog/wastes/pages/WastesPage').then((m) => ({ default: m.WastesPage })));
const CompaniesPage = lazy(() => import('@/modules/catalog/companies/pages/CompaniesPage').then((m) => ({ default: m.CompaniesPage })));
const CompanyDetailPage = lazy(() => import('@/modules/catalog/companies/pages/CompanyDetailPage').then((m) => ({ default: m.CompanyDetailPage })));
const PersonsPage = lazy(() => import('@/modules/catalog/persons/pages/PersonsPage').then((m) => ({ default: m.PersonsPage })));
const MarketPricesPage = lazy(() => import('@/modules/market-prices/pages/MarketPricesPage').then((m) => ({ default: m.MarketPricesPage })));
const CrmPage = lazy(() => import('@/modules/crm/pages/CrmPage').then((m) => ({ default: m.CrmPage })));
const HrPage = lazy(() => import('@/modules/hr/pages/HrPage').then((m) => ({ default: m.HrPage })));
const FlowTypesPage = lazy(() => import('@/modules/settings/flow-types/pages/FlowTypesPage').then((m) => ({ default: m.FlowTypesPage })));
const TariffTypesPage = lazy(() => import('@/modules/settings/tariff-types/pages/TariffTypesPage').then((m) => ({ default: m.TariffTypesPage })));
const TariffTypeDetailPage = lazy(() => import('@/modules/settings/tariff-types/pages/TariffTypeDetailPage').then((m) => ({ default: m.TariffTypeDetailPage })));
const AdminTenantsPage = lazy(() => import('@/modules/admin/pages/AdminTenantsPage').then((m) => ({ default: m.AdminTenantsPage })));
const AdminTenantUsersPage = lazy(() => import('@/modules/admin/pages/AdminTenantUsersPage').then((m) => ({ default: m.AdminTenantUsersPage })));
const AdminTenantModuleUsagePage = lazy(() => import('@/modules/admin/pages/AdminTenantModuleUsagePage').then((m) => ({ default: m.AdminTenantModuleUsagePage })));
const AdminManagersPage = lazy(() => import('@/modules/admin/pages/AdminManagersPage').then((m) => ({ default: m.AdminManagersPage })));
const AdminManagerTenantLinksPage = lazy(() => import('@/modules/admin/pages/AdminManagerTenantLinksPage').then((m) => ({ default: m.AdminManagerTenantLinksPage })));
const AdminReferralsPage = lazy(() => import('@/modules/admin/pages/AdminReferralsPage').then((m) => ({ default: m.AdminReferralsPage })));
const AdminMonitoringPage = lazy(() => import('@/modules/admin/pages/AdminMonitoringPage').then((m) => ({ default: m.AdminMonitoringPage })));
const AdminSupportPage = lazy(() => import('@/modules/admin/pages/AdminSupportPage').then((m) => ({ default: m.AdminSupportPage })));
const AdminSupportTicketPage = lazy(() => import('@/modules/admin/pages/AdminSupportTicketPage').then((m) => ({ default: m.AdminSupportTicketPage })));
const StockListPage = lazy(() => import('@/modules/stock/pages/StockListPage').then((m) => ({ default: m.StockListPage })));
const PickupSchedulePage = lazy(() => import('@/modules/pickup-schedule/pages/PickupSchedulePage').then((m) => ({ default: m.PickupSchedulePage })));
const ContainersListPage = lazy(() => import('@/modules/containers/pages/ContainersListPage').then((m) => ({ default: m.ContainersListPage })));
const ContainerDetailPage = lazy(() => import('@/modules/containers/pages/ContainerDetailPage').then((m) => ({ default: m.ContainerDetailPage })));
const CollectionsListPage = lazy(() => import('@/modules/collections/pages/CollectionsListPage').then((m) => ({ default: m.CollectionsListPage })));
const CollectionFormPage = lazy(() => import('@/modules/collections/pages/CollectionFormPage').then((m) => ({ default: m.CollectionFormPage })));
const OutgoingListPage = lazy(() => import('@/modules/outgoing/pages/OutgoingListPage').then((m) => ({ default: m.OutgoingListPage })));
const OutgoingFormPage = lazy(() => import('@/modules/outgoing/pages/OutgoingFormPage').then((m) => ({ default: m.OutgoingFormPage })));
const TreatmentListPage = lazy(() => import('@/modules/treatment/pages/TreatmentListPage').then((m) => ({ default: m.TreatmentListPage })));
const TreatmentFormPage = lazy(() => import('@/modules/treatment/pages/TreatmentFormPage').then((m) => ({ default: m.TreatmentFormPage })));
const DocumentsPage = lazy(() => import('@/modules/documents/pages/DocumentsPage').then((m) => ({ default: m.DocumentsPage })));
const ReportsPage = lazy(() => import('@/modules/reports/pages/ReportsPage').then((m) => ({ default: m.ReportsPage })));
const TenantProfilePage = lazy(() => import('@/modules/settings/tenant-profile/pages/TenantProfilePage').then((m) => ({ default: m.TenantProfilePage })));
const DocumentSequencesPage = lazy(() => import('@/modules/settings/document-sequences/pages/DocumentSequencesPage').then((m) => ({ default: m.DocumentSequencesPage })));
const AppConfigPage = lazy(() => import('@/modules/settings/pages/AppConfigPage').then((m) => ({ default: m.AppConfigPage })));
const MobileAppConfigPage = lazy(() => import('@/modules/settings/pages/MobileAppConfigPage').then((m) => ({ default: m.MobileAppConfigPage })));
const CompanyDocumentsPage = lazy(() => import('@/modules/settings/pages/CompanyDocumentsPage').then((m) => ({ default: m.CompanyDocumentsPage })));
const LogsPage = lazy(() => import('@/modules/settings/pages/LogsPage').then((m) => ({ default: m.LogsPage })));
const GdprCenterPage = lazy(() => import('@/modules/settings/pages/GdprCenterPage').then((m) => ({ default: m.GdprCenterPage })));
const ApiKeysPage = lazy(() => import('@/modules/settings/pages/ApiKeysPage').then((m) => ({ default: m.ApiKeysPage })));
const ScalesSettingsPage = lazy(() => import('@/modules/settings/pages/ScalesSettingsPage').then((m) => ({ default: m.ScalesSettingsPage })));
const SignaturePadSettingsPage = lazy(() => import('@/modules/settings/pages/SignaturePadSettingsPage').then((m) => ({ default: m.SignaturePadSettingsPage })));
const PadPairPage = lazy(() => import('@/modules/signature-pad/pages/PadPairPage').then((m) => ({ default: m.PadPairPage })));
const PadPage = lazy(() => import('@/modules/signature-pad/pages/PadPage').then((m) => ({ default: m.PadPage })));
const PickupRemoteLinkPublicPage = lazy(() => import('@/modules/pickup-remote-links/pages/PickupRemoteLinkPublicPage').then((m) => ({ default: m.PickupRemoteLinkPublicPage })));
const PortalLoginPage = lazy(() => import('@/modules/portal/pages/PortalLoginPage').then((m) => ({ default: m.PortalLoginPage })));
const PortalForgotPasswordPage = lazy(() => import('@/modules/portal/pages/PortalForgotPasswordPage').then((m) => ({ default: m.PortalForgotPasswordPage })));
const PortalSetPasswordPage = lazy(() => import('@/modules/portal/pages/PortalSetPasswordPage').then((m) => ({ default: m.PortalSetPasswordPage })));
const PortalLayout = lazy(() => import('@/modules/portal/PortalLayout').then((m) => ({ default: m.PortalLayout })));
const PortalPickupRequestsPage = lazy(() => import('@/modules/portal/pages/PortalPickupRequestsPage').then((m) => ({ default: m.PortalPickupRequestsPage })));
const PortalOffersPage = lazy(() => import('@/modules/portal/pages/PortalOffersPage').then((m) => ({ default: m.PortalOffersPage })));
const PortalContractsPage = lazy(() => import('@/modules/portal/pages/PortalContractsPage').then((m) => ({ default: m.PortalContractsPage })));
const PortalTransactionsPage = lazy(() => import('@/modules/portal/pages/PortalTransactionsPage').then((m) => ({ default: m.PortalTransactionsPage })));
const PortalDocumentsPage = lazy(() => import('@/modules/portal/pages/PortalDocumentsPage').then((m) => ({ default: m.PortalDocumentsPage })));
const NotificationPreferencesPage = lazy(() => import('@/modules/settings/pages/NotificationPreferencesPage').then((m) => ({ default: m.NotificationPreferencesPage })));
const NexusPage = lazy(() => import('@/modules/nexus/pages/NexusPage').then((m) => ({ default: m.NexusPage })));
const ContabilitatePage = lazy(() => import('@/modules/invoicing/pages/ContabilitatePage').then((m) => ({ default: m.ContabilitatePage })));
const InvoiceDetailPage = lazy(() => import('@/modules/invoicing/pages/InvoiceDetailPage').then((m) => ({ default: m.InvoiceDetailPage })));
const FinancialsPage = lazy(() => import('@/modules/financials/pages/FinancialsPage').then((m) => ({ default: m.FinancialsPage })));
const SettingsUsersPage = lazy(() => import('@/modules/settings/users/pages/SettingsUsersPage').then((m) => ({ default: m.SettingsUsersPage })));
const PrivacyPolicyPage = lazy(() => import('@/shared/components/PrivacyPolicyPage').then((m) => ({ default: m.PrivacyPolicyPage })));
const HelpPage = lazy(() => import('@/modules/help/pages/HelpPage').then((m) => ({ default: m.HelpPage })));
const SupportPage = lazy(() => import('@/modules/support/pages/SupportPage').then((m) => ({ default: m.SupportPage })));
const SupportTicketPage = lazy(() => import('@/modules/support/pages/SupportTicketPage').then((m) => ({ default: m.SupportTicketPage })));
const UpdatesPage = lazy(() => import('@/modules/support/pages/UpdatesPage').then((m) => ({ default: m.UpdatesPage })));

function PageLoader(): JSX.Element {
  return (
    <div className="flex min-h-[100dvh] items-center justify-center">
      <div className="size-7 animate-spin rounded-full border-2 border-muted border-t-primary" />
    </div>
  );
}

/**
 * Role-based path guard:
 *  - admin: only `/admin/*` and public auth routes; redirect elsewhere → /admin
 *  - operator: `/admin/*` is forbidden → redirect to /
 *  - mobile: lives entirely under `/m/*` (the field PWA); redirect elsewhere → /m
 */
function RoleGuard({ children }: { children: ReactNode }): JSX.Element {
  const { user, status } = useAuth();
  const location = useLocation();
  if (status !== 'authenticated' || !user) return <>{children}</>;

  const inAdmin = location.pathname.startsWith('/admin');
  const inMobile = location.pathname === '/m' || location.pathname.startsWith('/m/');
  const inSimpleMode = location.pathname.startsWith('/simple-mode');
  const inSetupWizard = location.pathname.startsWith('/setup-wizard');
  const isPublic = [
    '/login',
    '/mfa',
    '/mobile-only',
    '/change-password',
    '/forgot-password',
    '/reset-password',
    '/signup',
    '/verify-email',
    '/docs/legal',
    // F16-software "Telefon-semnătură" — the dedicated pad phone authenticates
    // via its own `device-pad` bearer cookie, never the normal session; it
    // must be reachable regardless of whatever role/session this browser
    // happens to also carry (e.g. a repurposed ex-mobile-role phone).
    '/pad-pair',
    '/pad',
    // F5 Portal parteneri — separate auth surface (`vanagreen_partner_session`
    // cookie, never the operator session), same rationale as /pad above: must
    // be reachable regardless of whatever operator role/session this browser
    // also happens to carry.
    '/portal',
    // Sprint "Semnare la distanță" — public, token-authenticated, no
    // session of its own; must stay reachable even when this browser
    // already carries an operator session (e.g. an operator opening their
    // own generated link to check it), same rationale as /pad/-portal above.
    '/pickup-link',
  ].some((p) => location.pathname.startsWith(p));
  if (isPublic) return <>{children}</>;
  if (user.role === 'admin' && !inAdmin) {
    return <Navigate to="/admin" replace />;
  }
  if (user.role === 'operator' && inAdmin) {
    return <Navigate to="/" replace />;
  }
  if (user.role === 'mobile' && !inMobile) {
    return <Navigate to="/m" replace />;
  }
  // PLAN.md item 8 — "Simple mode" is UI-only routing, not a permission
  // change: the user keeps full operator rights, this just keeps a
  // desktop-simple operator inside the dedicated wizard module instead of
  // the full sidebar app. Never applies to admin/mobile (mutually
  // exclusive roles already handled above).
  if (user.role === 'operator' && user.simpleMode && !inSimpleMode) {
    return <Navigate to="/simple-mode" replace />;
  }
  if (user.role === 'operator' && !user.simpleMode && inSimpleMode) {
    return <Navigate to="/" replace />;
  }
  // PLAN.md 2026-08-01, Faza A — setup wizard gate. Applies to EVERY new
  // tenant (admin-created too, not just self-serve — see tenants.setup_
  // wizard_completed's migration default), any operator, mirrors the
  // simpleMode block above exactly. Checked AFTER simpleMode redirects so
  // a simple-mode operator on an unfinished-wizard tenant still lands on
  // the wizard first (wizard takes priority — nothing is configured yet).
  //
  // `/settings/*` is deliberately exempt too, NOT just `/setup-wizard`
  // itself — the wizard's own checklist links out to the real Settings
  // pages (SetupWizardLayout.tsx), so blocking them here would make every
  // "Open" button bounce straight back to the wizard (found live,
  // 2026-08-03 — Stefan hit this testing the very first run). `/support`
  // is exempt for the same reason — the wizard's intro step links there
  // for reporting bugs while going through the wizard itself.
  const inSettings = location.pathname.startsWith('/settings');
  const inSupport = location.pathname.startsWith('/support');
  if (
    user.role === 'operator' &&
    user.tenant &&
    !user.tenant.setupWizardCompleted &&
    !inSetupWizard &&
    !inSettings &&
    !inSupport
  ) {
    return <Navigate to="/setup-wizard" replace />;
  }
  if (user.role === 'operator' && user.tenant?.setupWizardCompleted && inSetupWizard) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

export default function App(): JSX.Element {
  // ------------------------------------------------------------------
  // Radix overlay safety net.
  // Radix Dialog / DropdownMenu / Popover set `body { pointer-events: none }`
  // while a modal overlay is open, then clean it up on close. In edge cases
  // (ESC closing a dialog while a confirm sub-dialog is opening, focus
  // restore racing animations, etc.) the cleanup fails and the body stays
  // unclickable until the user reloads. We watch body.style and reset
  // `pointer-events` whenever no `[data-state="open"]` overlay is present.
  // ------------------------------------------------------------------
  useEffect(() => {
    const body = document.body;
    const reset = (): void => {
      if (body.style.pointerEvents !== 'none') return;
      const hasOpenOverlay = document.querySelector(
        '[data-state="open"][role="dialog"], [data-state="open"][role="alertdialog"], [data-state="open"][role="menu"], [data-state="open"][role="listbox"]',
      );
      if (!hasOpenOverlay) {
        body.style.pointerEvents = '';
      }
    };
    const observer = new MutationObserver(() => {
      // Defer until after Radix finishes its own DOM updates this tick.
      requestAnimationFrame(reset);
    });
    observer.observe(body, { attributes: true, attributeFilter: ['style'] });
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-state'],
      subtree: true,
    });
    return () => observer.disconnect();
  }, []);

  return (
    <>
      <RoleGuard>
        <Suspense fallback={<PageLoader />}>
          <Routes>
          <Route
            path="/login"
            element={
              <PublicOnly>
                <AuthLayout>
                  <LoginPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/signup"
            element={
              <PublicOnly>
                <AuthLayout>
                  <SignupPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/verify-email"
            element={
              <PublicOnly>
                <AuthLayout>
                  <VerifyEmailPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route path="/docs/legal/self-serve-signup-gdpr" element={<DataProcessingNoticePage />} />
          {/* F16-software "Telefon-semnătură" — dedicated pad phone, no session/role, own device-pad cookie */}
          <Route path="/pad-pair" element={<PadPairPage />} />
          <Route path="/pad" element={<PadPage />} />
          {/* F5 Portal parteneri — separate auth surface, own session cookie. */}
          <Route path="/pickup-link/:token" element={<PickupRemoteLinkPublicPage />} />
          <Route path="/portal/login" element={<PortalLoginPage />} />
          <Route path="/portal/forgot-password" element={<PortalForgotPasswordPage />} />
          <Route path="/portal/set-password" element={<PortalSetPasswordPage />} />
          <Route element={<PortalRequireAuth />}>
            <Route element={<PortalLayout />}>
              <Route path="/portal/pickup-requests" element={<PortalPickupRequestsPage />} />
              <Route path="/portal/offers" element={<PortalOffersPage />} />
              <Route path="/portal/contracts" element={<PortalContractsPage />} />
              <Route path="/portal/transactions" element={<PortalTransactionsPage />} />
              <Route path="/portal/documents" element={<PortalDocumentsPage />} />
              <Route path="/portal" element={<Navigate to="/portal/pickup-requests" replace />} />
            </Route>
          </Route>
          <Route
            path="/mfa"
            element={
              <PublicOnly>
                <AuthLayout>
                  <MfaChallengePage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/mfa/setup"
            element={
              <PublicOnly>
                <AuthLayout>
                  <MfaSetupPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/manager/select-tenant"
            element={
              <PublicOnly>
                <AuthLayout>
                  <ManagerSelectTenantPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/change-password"
            element={
              <PublicOnly>
                <AuthLayout>
                  <ForceChangePasswordPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <PublicOnly>
                <AuthLayout>
                  <ForgotPasswordPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/reset-password"
            element={
              <PublicOnly>
                <AuthLayout>
                  <ResetPasswordPage />
                </AuthLayout>
              </PublicOnly>
            }
          />
          <Route
            path="/mobile-only"
            element={
              <RequireAuth allowMobile roles={['mobile']}>
                <AuthLayout>
                  <MobileOnlyPage />
                </AuthLayout>
              </RequireAuth>
            }
          />

          {/* Mobile field app (PWA) — `mobile` role only, under /m/* */}
          <Route
            path="/m"
            element={
              <RequireAuth allowMobile roles={['mobile']}>
                <MobileLayout />
              </RequireAuth>
            }
          >
            <Route index element={<NewSheetPage />} />
            <Route path="program" element={<MobilePickupsPage />} />
            <Route path="containere" element={<MobileContainersPage />} />
            <Route path="persoana-noua" element={<NewPersonScanPage />} />
            <Route path="istoric" element={<MobileHistoryPage />} />
            <Route path="pontaj" element={<MobileCheckInPage />} />
          </Route>

          {/* PLAN.md item 8 — "Simple mode": UI-only wizard shell for
              desktop operators with user.simple_mode set. NOT a separate
              role/permission (RequireAuth roles=['operator']) — RoleGuard
              above handles the simple_mode redirect. */}
          <Route
            path="/simple-mode"
            element={
              <RequireAuth roles={['operator']}>
                <SimpleModeLayout />
              </RequireAuth>
            }
          >
            <Route index element={<SimpleModePage />} />
          </Route>

          {/* PLAN.md 2026-08-01, Faza A — mandatory setup wizard for any
              tenant with setup_wizard_completed=false. RoleGuard above
              handles the redirect; full-screen shell, no sidebar, mirrors
              SimpleModeLayout. */}
          <Route
            path="/setup-wizard"
            element={
              <RequireAuth roles={['operator']}>
                <SetupWizardLayout />
              </RequireAuth>
            }
          />

          {/* Operator app shell */}
          <Route
            element={
              <RequireAuth roles={['operator']}>
                <AppLayout />
              </RequireAuth>
            }
          >
            <Route path="/" element={<DashboardPage />} />
            <Route path="/catalog/wastes" element={<WastesPage />} />
            <Route path="/catalog/companies" element={<RequireFeature feature="companies"><CompaniesPage /></RequireFeature>} />
            <Route path="/catalog/companies/:id" element={<RequireFeature feature="companies"><CompanyDetailPage /></RequireFeature>} />
            <Route path="/catalog/persons" element={<RequireFeature feature="persons"><PersonsPage /></RequireFeature>} />
            <Route path="/catalog/market-prices" element={<RequireEntitlement module="market_prices"><MarketPricesPage /></RequireEntitlement>} />
            <Route path="/settings" element={<SettingsPage />}>
              <Route index element={<Navigate to="/settings/security" replace />} />
              {/* Profile & Access */}
              <Route path="security" element={<SecurityPage />} />
              <Route path="tenant-profile" element={<TenantProfilePage />} />
              <Route path="users" element={<SettingsUsersPage />} />
              {/* Operational */}
              <Route path="collection-config" element={<Navigate to="/settings/app-config" replace />} />
              <Route path="app-config" element={<AppConfigPage />} />
              <Route path="mobile-app" element={<MobileAppConfigPage />} />
              <Route path="notifications" element={<NotificationPreferencesPage />} />
              <Route path="flow-types" element={<FlowTypesPage />} />
              <Route path="tariff-types" element={<TariffTypesPage />} />
              <Route path="signature-pad" element={<RequireEntitlement module="signature_pad"><SignaturePadSettingsPage /></RequireEntitlement>} />
              <Route path="scales" element={<RequireEntitlement module="smart_scales"><ScalesSettingsPage /></RequireEntitlement>} />
              {/* Documents */}
              <Route path="document-sequences" element={<DocumentSequencesPage />} />
              <Route path="company-documents" element={<CompanyDocumentsPage />} />
              {/* Audit */}
              <Route path="logs" element={<LogsPage />} />
              {/* Compliance */}
              <Route path="gdpr" element={<GdprCenterPage />} />
              <Route path="api-keys" element={<ApiKeysPage />} />
            </Route>
            {/* Keep detail page outside layout — it has its own back button */}
            <Route path="/settings/tariff-types/:id" element={<TariffTypeDetailPage />} />
            <Route path="/operations/collections" element={<CollectionsListPage />} />
            <Route path="/operations/collections/new" element={<CollectionFormPage />} />
            <Route path="/operations/collections/:id" element={<CollectionFormPage />} />
            <Route path="/operations/outgoing" element={<OutgoingListPage />} />
            <Route path="/operations/outgoing/new" element={<OutgoingFormPage />} />
            <Route path="/operations/outgoing/:id" element={<OutgoingFormPage />} />
            <Route path="/operations/treatment" element={<RequireEntitlement module="treatment"><TreatmentListPage /></RequireEntitlement>} />
            <Route path="/operations/treatment/new" element={<RequireEntitlement module="treatment"><TreatmentFormPage /></RequireEntitlement>} />
            <Route path="/operations/treatment/:id" element={<RequireEntitlement module="treatment"><TreatmentFormPage /></RequireEntitlement>} />
            <Route path="/operations/stock" element={<StockListPage />} />
            <Route path="/operations/pickup-schedule" element={<RequireEntitlement module="pickup"><PickupSchedulePage /></RequireEntitlement>} />
            <Route path="/operations/documents" element={<DocumentsPage />} />
            <Route path="/containers" element={<ContainersListPage />} />
            <Route path="/containers/:id" element={<ContainerDetailPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/financials" element={<FinancialsPage />} />
            <Route path="/nexus" element={<RequireEntitlement module="nexus"><NexusPage /></RequireEntitlement>} />
            <Route path="/contabilitate" element={<RequireEntitlement module="accounting"><ContabilitatePage /></RequireEntitlement>} />
            <Route path="/contabilitate/facturi/:id" element={<RequireEntitlement module="accounting"><InvoiceDetailPage /></RequireEntitlement>} />
            <Route path="/crm" element={<RequireEntitlement module="crm"><CrmPage /></RequireEntitlement>} />
            <Route path="/hr" element={<RequireEntitlement module="hr"><HrPage /></RequireEntitlement>} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/:id" element={<SupportTicketPage />} />
            <Route path="/updates" element={<UpdatesPage />} />
          </Route>

          {/* Admin shell */}
          <Route
            element={
              <RequireAuth roles={['admin']}>
                <AdminLayout />
              </RequireAuth>
            }
          >
            <Route path="/admin" element={<Navigate to="/admin/tenants" replace />} />
            <Route path="/admin/tenants" element={<AdminTenantsPage />} />
            <Route path="/admin/tenants/:id/users" element={<AdminTenantUsersPage />} />
            <Route path="/admin/tenants/:id/module-usage" element={<AdminTenantModuleUsagePage />} />
            <Route path="/admin/managers" element={<AdminManagersPage />} />
            <Route path="/admin/managers/:id/tenant-links" element={<AdminManagerTenantLinksPage />} />
            <Route path="/admin/referrals" element={<AdminReferralsPage />} />
            <Route path="/admin/monitoring" element={<AdminMonitoringPage />} />
            <Route path="/admin/support" element={<AdminSupportPage />} />
            <Route path="/admin/support/:id" element={<AdminSupportTicketPage />} />
          </Route>

          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </RoleGuard>
      <CookieBanner />
    </>
  );
}
